// Servicio API para administración de usuarios
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Configurar axios con interceptores
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Interceptor para incluir token de autenticación
api.interceptors.request.use(
  (config) => {
    // Buscar token del dashboard primero, luego el token normal
    const token = localStorage.getItem('dashboardToken') || localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar respuestas y errores
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expirado o inválido
      localStorage.removeItem('access_token');
      localStorage.removeItem('dashboardToken');
      // Redirigir a dashboard login si estamos en el dashboard
      if (window.location.pathname.includes('/dashboard') || window.location.pathname.includes('/admin')) {
        window.location.href = '/dashboard/login';
      } else {
        window.location.href = '/login';
      }
    }
    
    const message = error.response?.data?.detail || 
                   error.response?.data?.message || 
                   error.message ||
                   'Error desconocido';
    
    return Promise.reject(new Error(message));
  }
);

export const adminAPI = {
  // === ESTADÍSTICAS ===
  async getStats() {
    const response = await api.get('/admin/stats');
    return response.data;
  },

  // === LÍDERES ===
  async getLideres(params = {}) {
    const response = await api.get('/admin/lideres', { params });
    return response.data;
  },

  async createLider(data) {
    const response = await api.post('/admin/lideres', data);
    return response.data;
  },

  async updateLider(id, data) {
    const response = await api.put(`/admin/lideres/${id}`, data);
    return response.data;
  },

  async deleteLider(id, force = false) {
    try {
      const response = await api.delete(`/admin/lideres/${id}`, { 
        params: { force } 
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async bulkActionLideres(data) {
    const response = await api.post('/admin/lideres/bulk-action', data);
    return response.data;
  },

  // === EMPLEADOS ===
  async getEmpleados(params = {}) {
    const response = await api.get('/admin/empleados', { params });
    return response.data;
  },

  async createEmpleado(data) {
    const response = await api.post('/admin/empleados', data);
    return response.data;
  },

  async updateEmpleado(id, data) {
    const response = await api.put(`/admin/empleados/${id}`, data);
    return response.data;
  },

  async deleteEmpleado(id) {
    try {
      const response = await api.delete(`/admin/empleados/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async bulkActionEmpleados(data) {
    const response = await api.post('/admin/empleados/bulk-action', data);
    return response.data;
  },

  // === ADMINISTRADORES ===
  async getAdmins(params = {}) {
    const response = await api.get('/admin/usuarios', { params });
    return response.data;
  },

  async createAdmin(data) {
    const response = await api.post('/admin/usuarios', data);
    return response.data;
  },

  async updateAdmin(id, data) {
    const response = await api.put(`/admin/usuarios/${id}`, data);
    return response.data;
  },

  async deleteAdmin(id) {
    try {
      const response = await api.delete(`/admin/usuarios/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async bulkActionAdmins(data) {
    const response = await api.post('/admin/usuarios/bulk-action', data);
    return response.data;
  },

  // === EXCEL ===
  async previewExcel(file) {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/admin/excel/preview', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  async importExcel(file, importType, updateExisting = false) {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/admin/excel/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      params: {
        import_type: importType,
        update_existing: updateExisting
      }
    });
    return response.data;
  },

  async exportToExcel(userType, filters = {}) {
    const endpoint = userType === 'admins' ? 'usuarios' : userType;
    
    const response = await api.get(`/admin/${endpoint}/export`, {
      params: filters,
      responseType: 'blob'
    });
    
    return response.data;
  },

  // === UTILIDADES ===
  async validateToken() {
    try {
      const response = await api.get('/admin/validate-token');
      return response.data;
    } catch (error) {
      return null;
    }
  },

  async getCurrentUser() {
    try {
      const response = await api.get('/admin/me');
      return response.data;
    } catch (error) {
      return null;
    }
  },

  // === BÚSQUEDAS Y FILTROS ===
  async searchUsers(userType, query, filters = {}) {
    const endpoint = userType === 'admins' ? 'usuarios' : userType;
    
    const response = await api.get(`/admin/${endpoint}`, {
      params: {
        search: query,
        ...filters,
        per_page: 50 // Límite para búsquedas
      }
    });
    return response.data;
  },

  async getLideresForSelect() {
    const response = await api.get('/admin/lideres', {
      params: {
        activo: true,
        per_page: 1000 // Obtener todos los líderes activos
      }
    });
    
    return response.data.items.map(lider => ({
      value: lider.id,
      label: `${lider.nombre_completo} (${lider.documento_identidad})`
    }));
  }
};

// Funciones de utilidad para formateo
export const formatters = {
  formatDate(dateString) {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  },

  formatUserStatus(isActive) {
    return isActive ? 'Activo' : 'Inactivo';
  },

  formatUserType(userType) {
    const types = {
      lideres: 'Líder',
      empleados: 'Empleado',
      admins: 'Administrador'
    };
    return types[userType] || userType;
  },

  truncateText(text, maxLength = 50) {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  }
};

// Validadores
export const validators = {
  validateEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  },

  validateDocument(document) {
    // Validar documento de identidad colombiano
    return /^[0-9]{6,12}$/.test(document);
  },

  validatePassword(password) {
    // Al menos 8 caracteres, una mayúscula, una minúscula y un número
    return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/.test(password);
  },

  validateRequired(value) {
    return value !== null && value !== undefined && value.toString().trim() !== '';
  }
};

export default adminAPI;